import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Plus, Users } from 'lucide-react';
import { StatsGrid, StatsGridSkeleton } from '../components/dashboard/StatsGrid';
import { ExportMenu } from '../components/export/ExportMenu';
import { StudentsEmptyState } from '../components/students/StudentsEmptyState';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useStudents } from '../contexts/StudentsContext';
import { initialsOf, orDash, relativeTime } from '../utils/format';
import { accountProfile } from '../utils/profile';
import { classDistribution, computeStats } from '../utils/stats';
export function Dashboard() {
  const {
    user
  } = useAuth();
  const {
    students,
    loading,
    openCreateForm,
    openDetail
  } = useStudents();
  const stats = useMemo(() => computeStats(students), [students]);
  const distribution = useMemo(() => classDistribution(students).slice(0, 6), [students]);
  const recent = useMemo(() => students.slice(0, 5), [students]);
  const maxCount = distribution[0]?.count ?? 1;
  const firstName = accountProfile(user).name.split(/[\s@]/)[0] || 'there';
  return <div className="mx-auto w-full max-w-7xl space-y-6" data-id="element-489" data-mp="2bovy1taxztvq">
      <section className="glass flex flex-col gap-5 rounded-3xl p-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-490" data-mp="2fpfusvrlsvhc">
        <div className="min-w-0" data-id="element-491" data-mp="1izp1gzv1ted4">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-492" data-mp="1j0qevazjecy0">
            {new Date().toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })}
          </p>
          <h2 className="mt-1.5 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-id="element-493" data-mp="1rykaejph92k4">
            Welcome back, <span className="gradient-text" data-id="element-494" data-mp="1wserrghm15g1">{firstName}</span>
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground" data-id="element-495" data-mp="108fh1vjqp56r">
            {stats.total === 0 ? 'Your school database is empty — add your first student to begin.' : `You are tracking ${stats.total} student${stats.total === 1 ? '' : 's'} across ${stats.classes} class${stats.classes === 1 ? '' : 'es'}.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-496" data-mp="2hagm9m6bvzsd">
          <ExportMenu data-id="element-497" data-mp="2cbdgfziixpjc" />
          <Button onClick={openCreateForm} className="h-9 rounded-xl brand-gradient px-4 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95" data-id="element-498" data-mp="2cd807ai27blu">
            <Plus className="mr-1.5 h-4 w-4" data-id="element-499" data-mp="1jo9g47udlzdw" />
            Add Student
          </Button>
        </div>
      </section>

      {loading ? <StatsGridSkeleton data-id="element-500" data-mp="1xobuhk3h6aoe" /> : <StatsGrid stats={stats} data-id="element-501" data-mp="1xko0yky15o9o" />}

      {!loading && stats.total === 0 ? <section className="glass rounded-3xl" data-id="element-502" data-mp="1tk41vbtwcakw">
          <StudentsEmptyState variant="no-records" onAddStudent={openCreateForm} data-id="element-503" data-mp="391mnk5xsc3j8" />
        </section> : <div className="grid gap-4 lg:grid-cols-5" data-id="element-504" data-mp="351c6ny8gfspl">
          <section className="glass rounded-3xl p-6 lg:col-span-2" aria-label="Students per class" data-id="element-505" data-mp="352x2dr6i9hp1">
            <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-506" data-mp="3cmg4hfrfy1nh">Students per class</h3>
            <p className="mt-1 text-xs text-muted-foreground" data-id="element-507" data-mp="3hgttn0fifcjp">Top classes by number of records</p>

            <div className="mt-5 space-y-4" data-id="element-508" data-mp="32oa3d0dgkzjo">
              {loading && Array.from({
            length: 4
          }).map((_, index) => <div key={index} className="space-y-2" data-id="element-509" data-mp="3vabb8fomyf73" data-mpi={index}>
                    <div className="shimmer h-2.5 w-1/3 rounded-full" data-id="element-510" data-mp="3vcxjkhjcoyqd" />
                    <div className="shimmer h-2 w-full rounded-full" data-id="element-511" data-mp="43qkyy6enbb4" />
                  </div>)}

              {!loading && distribution.map((bucket, index) => <div key={bucket.className} data-id="element-512" data-mp="3qia395yi3amv" data-mpi={index}>
                    <div className="flex items-center justify-between text-sm" data-id="element-513" data-mp="i4b640zslj7s">
                      <span className="truncate font-medium text-foreground" data-id="element-514" data-mp="i286qqcr2dqe">{bucket.className}</span>
                      <span className="font-mono text-xs text-muted-foreground" data-id="element-515" data-mp="2vbubcq407z9t">{bucket.count}</span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[var(--glass-hairline)]" data-id="element-516" data-mp="2qc7xqfk9kh0h">
                      <motion.div initial={{
                width: 0
              }} animate={{
                width: `${Math.max(bucket.count / maxCount * 100, 6)}%`
              }} transition={{
                duration: 0.6,
                delay: index * 0.05,
                ease: 'easeOut'
              }} className="h-full rounded-full brand-gradient" data-id="element-517" data-mp="2lcv1j47i5xl8" />
                    </div>
                  </div>)}
            </div>
          </section>

          <section className="glass rounded-3xl p-6 lg:col-span-3" aria-label="Recent additions" data-id="element-518" data-mp="2zdfjxz9i9re7">
            <div className="flex items-center justify-between" data-id="element-519" data-mp="nfdggzo4mza">
              <div data-id="element-520" data-mp="p07m5i2m3wm">
                <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-521" data-mp="piahkz8ngdi">Recent additions</h3>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-522" data-mp="3s0a0wm7468mx">The most recently recorded students</p>
              </div>
              <Link to="/students" className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" data-id="element-523" data-mp="86evojbe88h2">
                View all
                <ArrowRight className="h-3.5 w-3.5" data-id="element-524" data-mp="i5nm5271v4yo" />
              </Link>
            </div>

            <ul className="mt-4 divide-y divide-[var(--glass-hairline)]" data-id="element-525" data-mp="n4h00vq90xzd">
              {loading && Array.from({
            length: 4
          }).map((_, index) => <li key={index} className="flex items-center gap-3 py-3" data-id="element-526" data-mp="r3pvc50xt3x1" data-mpi={index}>
                    <div className="shimmer h-10 w-10 rounded-xl" data-id="element-527" data-mp="vx2ieapyx2no" />
                    <div className="flex-1 space-y-2" data-id="element-528" data-mp="10w5f97vso2kk">
                      <div className="shimmer h-3 w-1/3 rounded-full" data-id="element-529" data-mp="10xq54k75yxc3" />
                      <div className="shimmer h-2.5 w-1/4 rounded-full" data-id="element-530" data-mp="15w065bfkks5m" />
                    </div>
                  </li>)}

              {!loading && recent.map(student => <li key={student.id} data-id="element-531" data-mp="l5xkgnwb40un">
                    <button type="button" onClick={() => openDetail(student)} className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:opacity-80" data-id="element-532" data-mp="g6tavw5h013s">
                      <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-[var(--glass-hairline)] bg-white/60 text-[11px] font-semibold text-muted-foreground dark:bg-white/5" data-id="element-533" data-mp="q3fxapdg307x">
                        {student.fullNames.trim() ? initialsOf(student.fullNames) : '—'}
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-534" data-mp="18te3ezxomj8x">
                        <span className="block truncate text-sm font-medium text-foreground" data-id="element-535" data-mp="3fmhks32fmi7q">
                          {orDash(student.fullNames)}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-536" data-mp="1q4axmb88p0qh">
                          {[student.className.trim(), student.gender].filter(Boolean).join(' · ') || 'No class yet'}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground" data-id="element-537" data-mp="1gcx20ejesl2m">
                        {relativeTime(student.createdAtMs)}
                      </span>
                    </button>
                  </li>)}

              {!loading && recent.length === 0 && <li className="flex items-center gap-3 py-8 text-sm text-muted-foreground" data-id="element-538" data-mp="1bdk6l5eh25of">
                  <Users className="h-4 w-4" data-id="element-539" data-mp="xhzyl50a77vs" />
                  No records yet.
                </li>}
            </ul>
          </section>
        </div>}
    </div>;
}